/**
 * Downloads Management API Service
 * This service handles communication with the Azure Functions backend for downloads management.
 */

import axios from 'axios';

class DownloadsService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('DownloadsService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get user downloads
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise} - API response
   */
  async getUserDownloads(userId, filters = {}) {
    try {
      console.log('Getting downloads for user:', userId, 'with filters:', filters);
      
      const params = { userId, ...filters };
      const response = await axios.get(`${this.baseURL}/downloads/get`, { params });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error getting downloads:', error);
      throw error;
    }
  }

  /**
   * Record a download
   * @param {string} userId - User ID
   * @param {Object} downloadData - Download data
   * @returns {Promise} - API response
   */
  async recordDownload(userId, downloadData) {
    try {
      console.log('Recording download:', downloadData);
      
      const response = await axios.post(`${this.baseURL}/downloads/post`, {
        userId: userId,
        itemId: downloadData.itemId,
        itemType: downloadData.itemType, // 'video', 'ebook', 'certificate'
        title: downloadData.title,
        description: downloadData.description || '',
        category: downloadData.category || '',
        fileUrl: downloadData.fileUrl,
        fileSize: downloadData.fileSize,
        format: downloadData.format,
        downloadedAt: new Date().toISOString(),
        ...downloadData
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error recording download:', error);
      throw error;
    }
  }

  /**
   * Download a video file
   * @param {string} userId - User ID
   * @param {string} videoId - Video ID
   * @param {string} quality - Video quality (1080p, 720p, 480p)
   * @returns {Promise} - Download URL or streaming response
   */
  async downloadVideo(userId, videoId, quality = '720p') {
    try {
      console.log('Downloading video:', { userId, videoId, quality });
      
      const response = await axios.post(`${this.baseURL}/video/download`, {
        userId,
        videoId,
        quality,
        downloadedAt: new Date().toISOString()
      });
      
      // Record the download
      await this.recordDownload(userId, {
        itemId: videoId,
        itemType: 'video',
        title: response.data.title || 'Video Download',
        fileUrl: response.data.downloadUrl,
        fileSize: response.data.fileSize,
        format: quality
      });
      
      return response.data;
    } catch (error) {
      console.error('Error downloading video:', error);
      throw error;
    }
  }

  /**
   * Download an eBook
   * @param {string} userId - User ID
   * @param {string} ebookId - eBook ID
   * @param {string} format - Format (pdf, epub, mobi)
   * @returns {Promise} - Download URL
   */
  async downloadEbook(userId, ebookId, format = 'pdf') {
    try {
      console.log('Downloading ebook:', { userId, ebookId, format });
      
      const response = await axios.post(`${this.baseURL}/ebook/download`, {
        userId,
        ebookId,
        format,
        downloadedAt: new Date().toISOString()
      });
      
      // Record the download
      await this.recordDownload(userId, {
        itemId: ebookId,
        itemType: 'ebook',
        title: response.data.title || 'eBook Download',
        fileUrl: response.data.downloadUrl,
        fileSize: response.data.fileSize,
        format: format
      });
      
      return response.data;
    } catch (error) {
      console.error('Error downloading ebook:', error);
      throw error;
    }
  }

  /**
   * Download a certificate
   * @param {string} userId - User ID
   * @param {string} certificateId - Certificate ID
   * @returns {Promise} - Download URL
   */
  async downloadCertificate(userId, certificateId) {
    try {
      console.log('Downloading certificate:', { userId, certificateId });
      
      const response = await axios.post(`${this.baseURL}/certificate/download`, {
        userId,
        certificateId,
        downloadedAt: new Date().toISOString()
      });
      
      // Record the download
      await this.recordDownload(userId, {
        itemId: certificateId,
        itemType: 'certificate',
        title: response.data.title || 'Certificate Download',
        fileUrl: response.data.downloadUrl,
        fileSize: response.data.fileSize,
        format: 'pdf'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error downloading certificate:', error);
      throw error;
    }
  }

  /**
   * Clear download history
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async clearDownloadHistory(userId) {
    try {
      console.log('Clearing download history for user:', userId);
      
      const response = await axios.delete(`${this.baseURL}/downloads/deleteAll?userId=${userId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error clearing download history:', error);
      throw error;
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {Error} - Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data || 'Server error';
      return new Error(`API Error: ${message}`);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error: No response from server');
    } else {
      // Something else happened
      return new Error(`Request error: ${error.message}`);
    }
  }
}

// Export a singleton instance
const downloadsService = new DownloadsService();
export default downloadsService;
